import { apiEndpoint } from '@/lib/consts';
import fetchWithAuth from '@/lib/apis';

type FetchInit = Parameters<typeof fetch>[1];

// Strategy endpoints scope every request to the active org via this header.
// The backend resolves the active org from it (and verifies membership)
// rather than reading a stale `last_organization_id` off the user row, which
// is unreliable under admin impersonation and for users that never persisted
// a selection.
export const ORG_HEADER = 'X-Organization-Id';

// The org id is threaded explicitly from the calling component/query options
// (#4999) — no module-level state — and the request goes through
// ``fetchWithAuth`` so strategy calls get the same 401 → sign-in redirect and
// timeout handling as the rest of the app.
async function request<T>(path: string, orgId: number, init?: FetchInit): Promise<T> {
  const headers: Record<string, string> = {
    [ORG_HEADER]: String(orgId),
  };
  if (init?.headers) Object.assign(headers, init.headers as Record<string, string>);
  const response = await fetchWithAuth(`${apiEndpoint}${path}`, { ...init, headers });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Strategy request failed: ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/** Which KPI the user has chosen as their goal. AOV is never a focus — it's
 * a business input, not a Google Ads metric. */
export type FocusKpi = 'budget' | 'cpa' | 'conversions' | 'roas';

export interface AccountKpiTargetsWire {
  organisation_id: number;
  googleadsaccount_id: number;
  cpa: number;
  roas: number;
  spend: number;
  conversions: number;
  aov: number;
  focus_kpi: FocusKpi | null;
  /** The user's explicit goal for ``focus_kpi`` — authoritative source of
   * intent. Denormalised copy of the corresponding per-KPI column, written
   * at save time so downstream consumers don't have to switch on
   * ``focus_kpi`` to find the user's goal. */
  goal_value: number | null;
  updated_at: string;
}

export interface AccountKpiTargetsUpsert {
  googleadsaccount_id: number;
  cpa: number;
  roas: number;
  spend: number;
  conversions: number;
  aov: number;
  focus_kpi: FocusKpi | null;
  goal_value: number | null;
}

export function getAccountKpiTargets(
  orgId: number,
  googleadsaccount_id: number,
): Promise<AccountKpiTargetsWire> {
  return request<AccountKpiTargetsWire>(
    `/api/strategy/kpi-targets?googleadsaccount_id=${googleadsaccount_id}`,
    orgId,
  );
}

export function putAccountKpiTargets(
  orgId: number,
  body: AccountKpiTargetsUpsert,
): Promise<AccountKpiTargetsWire> {
  return request<AccountKpiTargetsWire>('/api/strategy/kpi-targets', orgId, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Opaque Goals-page state blob. Shape is owned by the frontend
 * (`strategy/index.tsx`); the server round-trips it verbatim. */
export interface StrategyGoalsStateBlob {
  enabledChannels?: Record<string, boolean>;
  completedGoals?: Record<string, boolean>;
}

export interface StrategyGoalsStateWire {
  organisation_id: number;
  state: StrategyGoalsStateBlob;
  updated_at: string;
}

export interface StrategyGoalsStateUpsert {
  state: StrategyGoalsStateBlob;
}

// Org is resolved server-side from the active-org header that `request`
// already sends; no account/org id in the path or body.
export function getStrategyGoalsState(orgId: number): Promise<StrategyGoalsStateWire> {
  return request<StrategyGoalsStateWire>('/api/strategy/goals-state', orgId);
}

export function putStrategyGoalsState(
  orgId: number,
  body: StrategyGoalsStateUpsert,
): Promise<StrategyGoalsStateWire> {
  return request<StrategyGoalsStateWire>('/api/strategy/goals-state', orgId, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// -------------------------------------------------------------------------
// Strategy-doc generator (call transcripts → strategy doc, scoped per
// org + business). The backend writes the markdown + audit sidecar to GCS
// and persists a row per run so the agent is fully auditable. Only the
// Context-tab read/approve/edit/upload surface is wired on this release line;
// the generation/build-sheet/plan endpoints land with their UI later.
// -------------------------------------------------------------------------

export type StrategyDocStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export type StrategyDocSource = 'generated' | 'uploaded';

export interface StrategyDocRunSummary {
  id: number;
  business_id: number;
  status: StrategyDocStatus;
  /** 'generated' = call-transcript pipeline; 'uploaded' = user-supplied doc. */
  source: StrategyDocSource;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** ISO timestamp of human sign-off, or null when not approved. */
  approved_at: string | null;
  error_text: string | null;
  transcript_count: number;
  output_markdown_gcs_uri: string | null;
  output_audit_gcs_uri: string | null;
  skills_used: string[];
}

export interface StrategyDocRunList {
  organisation_id: number;
  business_id: number;
  runs: StrategyDocRunSummary[];
}

export function listStrategyDocRuns(
  orgId: number,
  businessId: number,
): Promise<StrategyDocRunList> {
  return request<StrategyDocRunList>(`/api/strategy/doc/runs?business_id=${businessId}`, orgId);
}

export async function getStrategyDocMarkdown(orgId: number, runId: number): Promise<string> {
  // Plain-text response (text/markdown), not JSON, so the shared `request`
  // helper (which calls `.json()`) doesn't fit. Still goes through
  // `fetchWithAuth` for the same auth/timeout handling, with the org header.
  const response = await fetchWithAuth(`${apiEndpoint}/api/strategy/doc/runs/${runId}/markdown`, {
    headers: { Accept: 'text/markdown', [ORG_HEADER]: String(orgId) },
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Markdown fetch failed: ${response.status}`);
  }
  return response.text();
}

export async function saveStrategyDocMarkdown(
  orgId: number,
  runId: number,
  markdown: string,
): Promise<string> {
  // Mirrors getStrategyDocMarkdown: the endpoint returns the saved markdown as
  // plain text (not JSON). PUT the full edited document; the backend overwrites
  // the run's GCS blob.
  const response = await fetchWithAuth(`${apiEndpoint}/api/strategy/doc/runs/${runId}/markdown`, {
    method: 'PUT',
    headers: { Accept: 'text/markdown', [ORG_HEADER]: String(orgId) },
    body: JSON.stringify({ markdown }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Markdown save failed: ${response.status}`);
  }
  return response.text();
}

/** Approve (or revoke approval of) a strategy document. One endpoint toggles
 * both directions via the `approved` flag. */
export function setStrategyDocApproval(
  orgId: number,
  runId: number,
  approved: boolean,
): Promise<StrategyDocRunSummary> {
  return request<StrategyDocRunSummary>(`/api/strategy/doc/runs/${runId}/approval`, orgId, {
    method: 'POST',
    body: JSON.stringify({ approved }),
  });
}

/** Upload a finished strategy document directly (no transcripts, no agent).
 * It is stored as a succeeded run with source='uploaded' and appears alongside
 * generated docs. Multipart body — `fetchWithAuth` omits the JSON Content-Type
 * for FormData so the browser sets the multipart boundary. */
export async function uploadStrategyDoc(
  orgId: number,
  businessId: number,
  file: File,
): Promise<StrategyDocRunSummary> {
  const form = new FormData();
  form.append('file', file);
  const response = await fetchWithAuth(
    `${apiEndpoint}/api/strategy/doc/uploads?business_id=${businessId}`,
    {
      method: 'POST',
      body: form,
      headers: { [ORG_HEADER]: String(orgId) },
    },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Document upload failed: ${response.status}`);
  }
  return response.json() as Promise<StrategyDocRunSummary>;
}
